import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Card, CardContent, CardActions, Grid, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem,
  FormControl, InputLabel, Alert, CircularProgress, Divider, IconButton, Table,
  TableBody, TableCell, TableRow
} from '@mui/material';
import {
  Add as AddIcon, PersonAdd as DependentIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { getMyEnrollments, getAvailablePlans, createEnrollment } from '../api';

const STATUS_COLORS = {
  pending: 'warning',
  active: 'success',
  cancelled: 'error',
  declined: 'error',
  cobra: 'info',
};

const CATEGORY_ICONS = {
  health: '🏥', dental: '🦷', vision: '👁', life: '🛡', disability: '♿',
  '401k': '💰', roth_401k: '💰', hsa: '🏦', fsa: '🏦', other: '📋'
};

const COVERAGE_LABELS = {
  employee_only: 'Employee Only',
  employee_spouse: 'Employee + Spouse',
  employee_children: 'Employee + Children',
  family: 'Family',
};

const fmt = (val) => parseFloat(val || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export default function MyBenefits() {
  const [enrollments, setEnrollments] = useState([]);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [activePeriod, setActivePeriod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Enrollment dialog
  const [enrollDialog, setEnrollDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [enrollForm, setEnrollForm] = useState({
    effective_date: '', coverage_level: 'employee_only', dependents: []
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [enrollRes, plansRes] = await Promise.all([
        getMyEnrollments(), getAvailablePlans()
      ]);
      setEnrollments(enrollRes.data);
      setAvailablePlans(plansRes.data.plans || []);
      setActivePeriod(plansRes.data.activePeriod || null);
    } catch (err) {
      setError('Failed to load benefits data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openEnrollDialog = (plan) => {
    setSelectedPlan(plan);
    setEnrollForm({
      effective_date: '', coverage_level: plan.plan_type || 'employee_only', dependents: []
    });
    setEnrollDialog(true);
  };

  const addDependent = () => {
    setEnrollForm({
      ...enrollForm,
      dependents: [...enrollForm.dependents, { first_name: '', last_name: '', relationship: 'spouse', date_of_birth: '' }]
    });
  };

  const updateDependent = (idx, field, value) => {
    const deps = [...enrollForm.dependents];
    deps[idx] = { ...deps[idx], [field]: value };
    setEnrollForm({ ...enrollForm, dependents: deps });
  };

  const removeDependent = (idx) => {
    setEnrollForm({
      ...enrollForm,
      dependents: enrollForm.dependents.filter((_, i) => i !== idx)
    });
  };

  const submitEnrollment = async () => {
    try {
      await createEnrollment({
        benefit_plan_id: selectedPlan.id,
        enrollment_period_id: activePeriod?.id || null,
        effective_date: enrollForm.effective_date || null,
        coverage_level: enrollForm.coverage_level,
        dependents: enrollForm.dependents.filter(d => d.first_name && d.last_name)
      });
      setEnrollDialog(false);
      setSuccess('Enrollment submitted! Pending admin approval.');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit enrollment');
    }
  };

  // Group enrollments by status
  const activeEnrollments = enrollments.filter(e => e.status === 'active' || e.status === 'cobra');
  const pendingEnrollments = enrollments.filter(e => e.status === 'pending');
  const pastEnrollments = enrollments.filter(e => e.status === 'cancelled' || e.status === 'declined');

  // Plans not yet enrolled in
  const enrolledPlanIds = enrollments.filter(e => e.status !== 'cancelled' && e.status !== 'declined')
    .map(e => e.benefit_plan_id);
  const unenrolledPlans = availablePlans.filter(p => !enrolledPlanIds.includes(p.id));

  // Monthly totals
  const monthlyTotal = activeEnrollments.reduce((sum, e) => sum + parseFloat(e.employee_contribution || 0), 0);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" gutterBottom>My Benefits</Typography>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

      {activePeriod && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>{activePeriod.name}</strong> is open until {new Date(activePeriod.end_date).toLocaleDateString()}.
          You can enroll in available plans below.
        </Alert>
      )}

      {/* Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Active Plans</Typography>
              <Typography variant="h4">{activeEnrollments.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Pending</Typography>
              <Typography variant="h4" color="warning.main">{pendingEnrollments.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Monthly Cost</Typography>
              <Typography variant="h5">{fmt(monthlyTotal)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Annual Cost</Typography>
              <Typography variant="h5">{fmt(monthlyTotal * 12)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Active Enrollments */}
      {activeEnrollments.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom>Active Coverage</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {activeEnrollments.map(enrollment => (
              <Grid item xs={12} sm={6} md={4} key={enrollment.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6">
                        {CATEGORY_ICONS[enrollment.plan_category] || '📋'} {enrollment.plan_name}
                      </Typography>
                      <Chip label={enrollment.status} color={STATUS_COLORS[enrollment.status]} size="small" />
                    </Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {enrollment.provider_name || enrollment.plan_category}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ border: 0, pl: 0, py: 0.5 }}>Coverage</TableCell>
                          <TableCell sx={{ border: 0, py: 0.5 }} align="right">
                            {COVERAGE_LABELS[enrollment.coverage_level] || enrollment.coverage_level}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ border: 0, pl: 0, py: 0.5 }}>Your Cost</TableCell>
                          <TableCell sx={{ border: 0, py: 0.5 }} align="right">{fmt(enrollment.employee_contribution)}/mo</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ border: 0, pl: 0, py: 0.5 }}>Effective</TableCell>
                          <TableCell sx={{ border: 0, py: 0.5 }} align="right">
                            {enrollment.effective_date ? new Date(enrollment.effective_date).toLocaleDateString() : '—'}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                    {enrollment.dependents?.length > 0 && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" color="text.secondary">
                          Dependents: {enrollment.dependents.map(d => `${d.first_name} ${d.last_name}`).join(', ')}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Pending Enrollments */}
      {pendingEnrollments.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom>Pending Approval</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {pendingEnrollments.map(enrollment => (
              <Grid item xs={12} sm={6} md={4} key={enrollment.id}>
                <Card variant="outlined" sx={{ borderColor: 'warning.main' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="h6">
                        {CATEGORY_ICONS[enrollment.plan_category]} {enrollment.plan_name}
                      </Typography>
                      <Chip label="Pending" color="warning" size="small" />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {COVERAGE_LABELS[enrollment.coverage_level] || enrollment.coverage_level} — {fmt(enrollment.employee_contribution)}/mo
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Submitted {new Date(enrollment.created_at).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Available Plans */}
      {unenrolledPlans.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom>Available Plans</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {unenrolledPlans.map(plan => (
              <Grid item xs={12} sm={6} md={4} key={plan.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6">
                      {CATEGORY_ICONS[plan.category]} {plan.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {plan.provider_name || plan.category} — {plan.plan_type?.replace(/_/g, ' ')}
                    </Typography>
                    {plan.description && (
                      <Typography variant="body2" sx={{ mb: 1 }}>{plan.description}</Typography>
                    )}
                    <Typography variant="body2">
                      Employee: <strong>{fmt(plan.monthly_employee_cost)}</strong>/mo
                      {parseFloat(plan.monthly_employer_cost) > 0 && (
                        <> | Employer: <strong>{fmt(plan.monthly_employer_cost)}</strong>/mo</>
                      )}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" variant="outlined" startIcon={<AddIcon />}
                      onClick={() => openEnrollDialog(plan)}
                      disabled={!activePeriod}>
                      {activePeriod ? 'Enroll' : 'No Open Period'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Past Enrollments */}
      {pastEnrollments.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom color="text.secondary">Past Enrollments</Typography>
          <Grid container spacing={2}>
            {pastEnrollments.map(enrollment => (
              <Grid item xs={12} sm={6} md={4} key={enrollment.id}>
                <Card variant="outlined" sx={{ opacity: 0.6 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography>{enrollment.plan_name}</Typography>
                      <Chip label={enrollment.status} color={STATUS_COLORS[enrollment.status]} size="small" />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Enrollment Dialog */}
      <Dialog open={enrollDialog} onClose={() => setEnrollDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enroll in {selectedPlan?.name}</DialogTitle>
        <DialogContent>
          {selectedPlan && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <Alert severity="info" variant="outlined">
                  Monthly cost: <strong>{fmt(selectedPlan.monthly_employee_cost)}</strong>
                  {selectedPlan.tax_treatment === 'pre_tax' && ' (pre-tax)'}
                  {selectedPlan.tax_treatment === 'post_tax' && ' (post-tax)'}
                </Alert>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Coverage Level</InputLabel>
                  <Select value={enrollForm.coverage_level} label="Coverage Level"
                    onChange={e => setEnrollForm({ ...enrollForm, coverage_level: e.target.value })}>
                    <MenuItem value="employee_only">Employee Only</MenuItem>
                    <MenuItem value="employee_spouse">Employee + Spouse</MenuItem>
                    <MenuItem value="employee_children">Employee + Children</MenuItem>
                    <MenuItem value="family">Family</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Effective Date" type="date" value={enrollForm.effective_date}
                  onChange={e => setEnrollForm({ ...enrollForm, effective_date: e.target.value })}
                  InputLabelProps={{ shrink: true }} />
              </Grid>

              {/* Dependents */}
              {enrollForm.coverage_level !== 'employee_only' && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">Dependents</Typography>
                    <Button size="small" startIcon={<DependentIcon />} onClick={addDependent}>
                      Add Dependent
                    </Button>
                  </Box>
                  {enrollForm.dependents.map((dep, idx) => (
                    <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12} sm={3}>
                          <TextField fullWidth size="small" label="First Name" value={dep.first_name}
                            onChange={e => updateDependent(idx, 'first_name', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField fullWidth size="small" label="Last Name" value={dep.last_name}
                            onChange={e => updateDependent(idx, 'last_name', e.target.value)} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Relationship</InputLabel>
                            <Select value={dep.relationship} label="Relationship"
                              onChange={e => updateDependent(idx, 'relationship', e.target.value)}>
                              <MenuItem value="spouse">Spouse</MenuItem>
                              <MenuItem value="child">Child</MenuItem>
                              <MenuItem value="domestic_partner">Domestic Partner</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={5} sm={2}>
                          <TextField fullWidth size="small" label="DOB" type="date" value={dep.date_of_birth || ''}
                            onChange={e => updateDependent(idx, 'date_of_birth', e.target.value)}
                            InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={1}>
                          <IconButton size="small" color="error" onClick={() => removeDependent(idx)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrollDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitEnrollment}>Submit Enrollment</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
